import React, { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useCookieConsent } from '@/context/CookieConsentContext'
import { useT } from '@/context/TranslateCtx'
import Privacy from '@/components/Privacy.jsx'
import CookiePolicy from '@/components/CookiePolicy.jsx'

export default function CookieBanner({ onAccept, onReject }) {
  const { prefs = {}, savePrefs } = useCookieConsent()
  const { t } = useT()
  const [showPrefs, setShowPrefs] = useState(false)
  const [policy, setPolicy] = useState(null)
  const slide = {
    hidden: { y: 120, opacity: 0 },
    visible: { y: 0, opacity: 1, transition: { type: 'spring', stiffness: 200, damping: 24 } },
    exit: { y: 120, opacity: 0, transition: { duration: 0.25 } }
  }
  const closePolicy = () => setPolicy(null)

  return (
    <AnimatePresence>
      {!policy && (
        <motion.div variants={slide} initial="hidden" animate="visible" exit="exit" className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[90] w-[95%] md:w-[42rem] bg-white border rounded-3xl shadow-2xl p-6 md:p-8">
          {showPrefs ? (
            <Preferences prefs={prefs} savePrefs={p => { savePrefs(p); setShowPrefs(false) }} onCancel={() => setShowPrefs(false)} />
          ) : (
            <Notice
              t={t}
              onAccept={onAccept}
              onReject={onReject}
              onManage={() => setShowPrefs(true)}
              openPrivacy={() => setPolicy('privacy')}
              openCookie={() => setPolicy('cookie')}
            />
          )}
        </motion.div>
      )}
      {policy && (
        <motion.div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[95] flex items-center justify-center" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
          <motion.div className="relative bg-white rounded-3xl shadow-2xl w-[90%] md:w-[80%] max-h-[90vh] overflow-y-auto" variants={slide} initial="hidden" animate="visible" exit="exit">
            {policy === 'privacy' ? <Privacy onClose={closePolicy} /> : <CookiePolicy onClose={closePolicy} />}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

function Notice({ t, onAccept, onReject, onManage, openPrivacy, openCookie }) {
  return (
    <>
      <h3 className="text-xl md:text-2xl font-bold mb-3">{t('cookie.title')}</h3>
      <p className="text-base md:text-lg text-gray-800 leading-relaxed mb-6">
        {t('cookie.notice')}&nbsp;
        <button onClick={openPrivacy} className="text-primary underline">{t('cookie.link.privacy')}</button>&nbsp;|&nbsp;
        <button onClick={openCookie} className="text-primary underline">{t('cookie.link.cookie')}</button>
      </p>
      <div className="grid sm:grid-cols-3 gap-4">
        <button onClick={onReject} className="py-3 rounded-full bg-gray-200 text-base md:text-lg hover:bg-gray-300">{t('cookie.btn.reject')}</button>
        <button onClick={onManage} className="py-3 rounded-full bg-gray-100 text-base md:text-lg hover:bg-gray-200">{t('cookie.btn.manage')}</button>
        <button onClick={onAccept} className="py-3 rounded-full bg-primary text-white text-base md:text-lg hover:bg-secondary">{t('cookie.btn.accept')}</button>
      </div>
    </>
  )
}

function Preferences({ prefs = {}, savePrefs, onCancel }) {
  const { t } = useT()
  const [local, setLocal] = useState({ essential: true, functional: false, analytics: false, marketing: false, ...prefs })
  const toggle = k => setLocal({ ...local, [k]: !local[k] })
  const opts = [
    { k: 'essential', label: t('cookie.pref.essential'), lock: true },
    { k: 'functional', label: t('cookie.pref.functional'), lock: false },
    { k: 'analytics', label: t('cookie.pref.analytics'), lock: false },
    { k: 'marketing', label: t('cookie.pref.marketing'), lock: false },
  ]
  return (
    <>
      <h3 className="text-xl md:text-2xl font-bold mb-4">{t('cookie.pref.title')}</h3>
      <div className="space-y-4 mb-8">
        {opts.map(o => (
          <label key={o.k} className="flex items-center gap-3 text-base md:text-lg">
            <input type="checkbox" checked={!!local[o.k]} disabled={o.lock} onChange={() => toggle(o.k)} className="w-5 h-5 accent-primary" />
            {o.label}
          </label>
        ))}
      </div>
      <div className="flex gap-4">
        <button onClick={() => savePrefs(local)} className="flex-1 py-3 rounded-full bg-primary text-white text-base md:text-lg hover:bg-secondary">{t('cookie.pref.save')}</button>
        <button onClick={onCancel} className="flex-1 py-3 rounded-full bg-gray-200 text-base md:text-lg hover:bg-gray-300">{t('cookie.pref.cancel')}</button>
      </div>
    </>
  )
}
